import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

function AnimatedBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let frame;
    let w = canvas.width = canvas.offsetWidth;
    let h = canvas.height = canvas.offsetHeight;
    const cx = w / 2, cy = h / 2;

    // Stars
    const stars = Array.from({ length: 60 }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      r: Math.random() * 1.5 + 0.5,
      a: Math.random(), da: (Math.random() * 0.008 + 0.003) * (Math.random() > 0.5 ? 1 : -1),
    }));

    // Node recipients
    const nodes = [
      { x: 0.1, y: 0.2 }, { x: 0.88, y: 0.18 }, { x: 0.07, y: 0.65 },
      { x: 0.9, y: 0.72 }, { x: 0.25, y: 0.08 }, { x: 0.75, y: 0.9 },
      { x: 0.13, y: 0.85 }, { x: 0.85, y: 0.45 }, { x: 0.42, y: 0.05 },
      { x: 0.6, y: 0.93 }, { x: 0.03, y: 0.45 }, { x: 0.95, y: 0.52 },
    ].map(n => ({ ...n, px: n.x * w, py: n.y * h, pulse: Math.random() * Math.PI * 2 }));

    // Envelopes
    const envCount = 8;
    const envelopes = Array.from({ length: envCount }, (_, i) => {
      const angle = (i / envCount) * Math.PI * 2 + Math.random() * 0.3;
      return { angle, progress: (i / envCount), speed: 0.004 + Math.random() * 0.002 };
    });

    function drawEnvelope(x, y, size, alpha, angle) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.strokeStyle = '#e8a0a8';
      ctx.fillStyle = 'rgba(139,26,46,0.15)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.roundRect(-size, -size * 0.7, size * 2, size * 1.4, 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-size, -size * 0.7);
      ctx.lineTo(0, size * 0.2);
      ctx.lineTo(size, -size * 0.7);
      ctx.stroke();
      ctx.restore();
    }

    function draw(t) {
      ctx.clearRect(0, 0, w, h);

      // Stars
      stars.forEach(s => {
        s.a = Math.max(0.05, Math.min(0.7, s.a + s.da));
        if (s.a <= 0.05 || s.a >= 0.7) s.da *= -1;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${s.a})`;
        ctx.fill();
      });

      // Hub pulsing rings
      for (let r = 0; r < 3; r++) {
        const ringT = ((t * 0.0008 + r * 0.33) % 1);
        const radius = 36 + ringT * 80;
        const alpha = (1 - ringT) * 0.35;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(139,26,46,${alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Connection lines from hub to nodes
      nodes.forEach(n => {
        const dist = Math.hypot(n.px - cx, n.py - cy);
        const alpha = 0.08 + Math.sin(t * 0.001 + n.pulse) * 0.04;
        const grad = ctx.createLinearGradient(cx, cy, n.px, n.py);
        grad.addColorStop(0, `rgba(139,26,46,${alpha * 2})`);
        grad.addColorStop(1, `rgba(139,26,46,0)`);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(n.px, n.py);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 0.8;
        ctx.setLineDash([4, 8]);
        ctx.stroke();
        ctx.setLineDash([]);

        // Node dot
        const pulse = 0.5 + Math.sin(t * 0.002 + n.pulse) * 0.5;
        ctx.beginPath();
        ctx.arc(n.px, n.py, 4 + pulse * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139,26,46,${0.3 + pulse * 0.4})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.px, n.py, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#8B1A2E';
        ctx.fill();
      });

      // Envelopes flying out
      envelopes.forEach(env => {
        env.progress = (env.progress + env.speed) % 1;
        const p = env.progress;
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        const startR = 36, endR = Math.min(w, h) * 0.48;
        const r = startR + eased * (endR - startR);
        const x = cx + Math.cos(env.angle) * r;
        const y = cy + Math.sin(env.angle) * r;
        const alpha = p < 0.15 ? p / 0.15 : p > 0.75 ? 1 - (p - 0.75) / 0.25 : 1;
        const size = 9 + eased * 5;
        drawEnvelope(x, y, size, alpha * 0.9, env.angle + Math.PI / 2);
      });

      // Hub core
      ctx.beginPath();
      ctx.arc(cx, cy, 32, 0, Math.PI * 2);
      ctx.fillStyle = '#8B1A2E';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, 32, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Hub envelope icon
      ctx.save();
      ctx.translate(cx, cy);
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.roundRect(-12, -9, 24, 18, 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-12, -9);
      ctx.lineTo(0, 3);
      ctx.lineTo(12, -9);
      ctx.stroke();
      ctx.restore();

      frame = requestAnimationFrame(draw);
    }

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 16,
    }} />
  );
}

export default function Login() {
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [mode, setMode] = useState('login');

  async function handleLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch {
      setError('Invalid email or password. Please try again.');
    }
    setLoading(false);
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch {
      setError('Could not send reset email. Check the address and try again.');
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#111828', padding: 20,
    }}>
      {/* Full screen animated background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
        <AnimatedBackground />
      </div>

      {/* Login card */}
      <div style={{
        position: 'relative', zIndex: 10,
        background: 'rgba(255,255,255,0.97)',
        borderRadius: 16, padding: '36px 32px',
        width: 400, maxWidth: '92%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(8px)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 8, paddingBottom: 22, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <img src="/cmf-logo.png" alt="CM Fellowship Maharashtra"
            style={{ width: 200, height: 'auto', display: 'block', margin: '0 auto' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, background: '#8B1A2E', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18 }}>✉</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a2340' }}>CMFP Chitthi</div>
          </div>
          <div style={{ fontSize: 11, color: '#9aa5b8', marginTop: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Chitthi by Team CMFP
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {resetSent && <div className="alert alert-success">Password reset email sent! Check your inbox.</div>}

        {mode === 'login' ? (
          <form onSubmit={handleLogin} style={{ marginTop: 20 }}>
            <div className="field">
              <label className="field-label">Email address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@cmfellowship.in" required />
            </div>
            <div className="field">
              <label className="field-label">Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#9aa5b8' }} onClick={() => setMode('reset')}>
                Forgot password?
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleReset} style={{ marginTop: 20 }}>
            <p style={{ fontSize: 13, color: '#4a5568', marginBottom: 14 }}>
              Enter your email and we'll send a password reset link.
            </p>
            <div className="field">
              <label className="field-label">Email address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@cmfellowship.in" required />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#9aa5b8' }} onClick={() => setMode('login')}>
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
